import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { notify } from 'services';
import { TRANSPORT_ROUTES } from 'routes/transport-routes';
import { useTranslation } from 'hooks';
import {
  useFetchTransportRoute,
  useUpdateTransportRoute,
} from 'hooks/api/tranpsort-routes';
import { SettingLayout } from 'layouts/SettingLayout';
import { MainContainer } from 'components/shared';
import {
  TransportRouteMap
} from 'pages/Settings/transport-routes/shared-components'

import {
  useForwardTripState,
  useBackwardTripState,
} from '../hooks';
import {
  PageHeader,
  UpdateRouteForm,
  type UpdateRouteFormRef,
  UpdateRouteSubmissionBar,
} from './components';
import { translations } from './translations';

type RouteParams = {
  id: string;
}

export const UpdateTransportRoutePage = () => {
  const { getTranslation: t } = useTranslation(translations);
  const { id: routeId } = useParams<RouteParams>();
  const navigate = useHistory().push;
  const { clearForwardTripState } = useForwardTripState();
  const { clearBackwardTripState } = useBackwardTripState();
  const { fetchTransportRoute } = useFetchTransportRoute();
  const { updateTransportRoute } = useUpdateTransportRoute();

  const updateRouteFormRef = React.useRef<UpdateRouteFormRef>(null);

  const navigateToListRoutesPage = React.useCallback(() => {
    navigate(TRANSPORT_ROUTES.listRoutes().path);
  }, [navigate]);

  const transportRouteQuery = useQuery({
    queryKey: ['fetch-transport-route', routeId],
    queryFn: () => fetchTransportRoute(routeId),
    refetchOnWindowFocus: false,
  });

  const updateTransportRouteMutation = useMutation({
    mutationKey: ['update-transport-route', routeId],
    mutationFn: updateTransportRoute,
    onSuccess: () => {
      notify('success', t('routeUpdateSuccessMessage'));
      navigateToListRoutesPage();
    },
    onError: (error: any) => {
      notify('error', error?.message);
    },
  });

  const executeFormSubmission = React.useCallback(async () => {
    try {
      const data = await updateRouteFormRef.current?.getFormData();
      if (data) {
        updateTransportRouteMutation.mutate({
          transportRouteId: routeId,
          data,
        });
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  React.useEffect(() => {
    return () => {
      clearForwardTripState();
      clearBackwardTripState();
    }
  }, []);

  return (
    <MainContainer>
      <SettingLayout>
        <PageHeader
          transportRouteId={routeId}
          transportRoute={transportRouteQuery?.data}
          loading={transportRouteQuery.isLoading}
          className={'mb-6 -mt-6'}
        />
        <UpdateRouteForm
          ref={updateRouteFormRef}
          transportRoute={transportRouteQuery?.data}
          loading={transportRouteQuery.isLoading}
          className={'mb-6'}
        />
        <TransportRouteMap className={'mb-6'} />
        <UpdateRouteSubmissionBar
          onClickSubmit={executeFormSubmission}
          onClickCancel={navigateToListRoutesPage}
          loading={
            transportRouteQuery.isLoading ||
            updateTransportRouteMutation.isLoading
          }
        />
      </SettingLayout>
    </MainContainer>
  );
};